/**
 * Header stats sopra la lista preventivi nel project detail.
 * Mostra: count + valore per stato (vinto/aperto/perso), m² totali,
 * preventivi con prezzi disallineati + bottone "Aggiorna tutti".
 */
import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trophy, Send, XCircle, Target, RefreshCw } from 'lucide-react';
import { useEstimates } from '@/hooks/useEstimates';
import { useRefreshAllStaleEstimates } from '@/hooks/useRefreshAllStaleEstimates';

function euro(n: number): string {
  return `€ ${Math.round(n).toLocaleString('it-IT')}`;
}

interface ProjectStatsHeaderProps {
  projectId: string;
}

const ProjectStatsHeader: React.FC<ProjectStatsHeaderProps> = ({ projectId }) => {
  const { estimates } = useEstimates(projectId);
  const { refreshAll, isRefreshing } = useRefreshAllStaleEstimates();

  const stats = useMemo(() => {
    const won = estimates.filter(e => e.status === 'won' || e.status === 'contracted');
    const open = estimates.filter(e => e.status === 'sent' || e.status === 'pending' || e.status === 'draft');
    const lost = estimates.filter(e => e.status === 'lost');
    const wonValue = won.reduce((s, e) => s + (e.totalAmount ?? 0), 0);
    const openValue = open.reduce((s, e) => s + (e.totalAmount ?? 0), 0);
    const lostValue = lost.reduce((s, e) => s + (e.totalAmount ?? 0), 0);
    // I non-locked sono candidati al "Aggiorna prezzi tutti"
    const refreshableIds = estimates
      .filter(e => e.status !== 'won' && e.status !== 'lost' && e.status !== 'contracted')
      .map(e => e.id);
    return {
      total: estimates.length,
      won: won.length,
      open: open.length,
      lost: lost.length,
      wonValue, openValue, lostValue,
      refreshableIds,
    };
  }, [estimates]);

  if (stats.total === 0) return null;

  return (
    <div className="mb-6 space-y-3">
      <div className="grid gap-3 md:grid-cols-3">
        <StatCard
          icon={<Trophy className="h-4 w-4 text-emerald-600" />}
          label="Vinti"
          count={stats.won}
          value={stats.wonValue}
          bgClass="border-emerald-200/50 bg-emerald-50/40"
        />
        <StatCard
          icon={<Send className="h-4 w-4 text-amber-600" />}
          label="Aperti"
          count={stats.open}
          value={stats.openValue}
          bgClass="border-amber-200/50 bg-amber-50/40"
        />
        <StatCard
          icon={<XCircle className="h-4 w-4 text-red-600" />}
          label="Persi"
          count={stats.lost}
          value={stats.lostValue}
          bgClass="border-red-200/50 bg-red-50/40"
        />
      </div>
      {stats.refreshableIds.length > 0 && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            disabled={isRefreshing}
            onClick={() => refreshAll({ estimateIds: stats.refreshableIds })}
            className="gap-1.5"
            title="Ricalcola i prezzi di tutti i preventivi non chiusi del progetto"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Aggiornamento…' : `Aggiorna prezzi (${stats.refreshableIds.length})`}
          </Button>
        </div>
      )}
    </div>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  value: number;
  bgClass: string;
}
const StatCard: React.FC<StatCardProps> = ({ icon, label, count, value, bgClass }) => (
  <Card className={bgClass}>
    <CardContent className="p-4 flex items-center justify-between">
      <div>
        <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className="text-2xl font-bold mt-1 tabular-nums">{count}</div>
      </div>
      <div className="text-right">
        <Target className="h-3.5 w-3.5 text-muted-foreground/40 ml-auto mb-1" />
        <div className="text-sm font-semibold tabular-nums">{euro(value)}</div>
      </div>
    </CardContent>
  </Card>
);

export default ProjectStatsHeader;
